/**
 * Bokja account drawer: the "Join the collective" row.
 *
 * James, 2026-08-13 (Arket menu reference): the signed-out drawer leads with two stacked
 * primary rows, Sign in then Join the collective.
 *
 * THE STANDING RULING IS WHY THIS FILE EXISTS: one membership flow, never a second capture
 * form. The row does NOT render its own email field. It opens the SAME <dialog> the
 * announcement bar opens (blocks/bokja-collective-invite.liquid) — same copy, same
 * `contact[tags]` value, same Klaviyo chain. If that dialog ever needs a second trigger
 * elsewhere, add it here, not another form.
 *
 * The block cannot be addressed by id from the header: its dialog id is built from the
 * announcement-bar block id, which the header section cannot see. The stable handle is the
 * class `dialog.bokja-collective-invite`, so that is what this resolves.
 *
 * FALLBACK. The invite block sits in header-group and therefore renders on every page, but
 * it can be removed in the theme editor at any time, and a row that opens nothing is worse
 * than no row. So the row HIDES ITSELF when the dialog is absent, rather than falling back
 * to the footer newsletter: that form carries the `newsletter` tag only, not `collective`,
 * so pointing at it would put someone in the wrong flow with no 10% chain — a second
 * membership path by the back door, which is the thing the ruling forbids.
 *
 * THE CHOREOGRAPHY INVERTED ON 2026-08-14 (James: the invite as a second right-side panel
 * OVER the drawer), AND INVERTED AGAIN ON 2026-08-24 (Malou: "same for join the collective"
 * - by then every other door had the replace-not-stack swap, and the drawer joining it is
 * the consistent read). Now: opening from the drawer visibility-swaps the drawer away (the
 * same `bokja-signin-open` class the sign-in sheet uses) and the invite appears as THE
 * centred pop-up; closing it brings the drawer back where the visitor left it. The
 * right-side panel class and its origin-restore machinery were removed with the change -
 * they had no remaining caller. If the panel treatment is ever wanted back, it is in git
 * before this date.
 *
 * THE OLD ORDER'S REASON WENT WITH IT. It closed the drawer first to avoid "stacking two
 * modal <dialog>s with scroll locks". Stacking them is fine, and it is fine for reasons
 * worth writing down rather than re-deriving:
 *   - The scroll lock is Horizon's, not the platform's: DialogComponent.showDialog() pins
 *     `body { position: fixed }` and restores it in closeDialog(). Nothing here calls either,
 *     so the lock is taken once, by the drawer, and released once, by the drawer.
 *   - The drawer's click-outside and Escape handlers are bound to the fabric-drawer-component
 *     ELEMENT, and the invite dialog is in the announcement bar — outside it. So neither a
 *     click inside the panel nor an Escape while it is focused reaches them. MEASURED ON THE
 *     PREVIEW, with a control: an Escape keydown dispatched from inside the panel left the
 *     drawer open, and the same event dispatched from inside the DRAWER closed it — so the
 *     first result is isolation, not a listener that was never wired. What the harness could
 *     not exercise is the browser's own half (a real key press closing the topmost dialog
 *     only): synthetic events do not drive it, and the render harness could not deliver a
 *     trusted one. That part is spec behaviour, and it is stated here as spec, not as a
 *     measurement.
 *   - Unlike the sign-in sheet (which lives in a shadow root INSIDE the drawer's <dialog> and
 *     is torn down with it), this dialog is a sibling in the DOM. Nothing about the drawer's
 *     state can destroy it.
 *
 * FOCUS. The panel is a modal dialog, so the browser traps focus in it and restores focus to
 * the row on close; the explicit `row.focus()` below is the same belt-and-braces restore
 * fabric-drawer.js does for its own out-of-component trigger, and it is what makes the drawer
 * beneath immediately keyboard-usable again.
 */

const DIALOG = 'dialog.bokja-collective-invite';
const ROW = '[data-bokja-account-collective]';
const GROUP = '[data-bokja-account-collective-group]';
/** @returns {HTMLDialogElement | null} */
const inviteDialog = () => /** @type {HTMLDialogElement | null} */ (document.querySelector(DIALOG));

/** Show the collective rows only where the dialog they open actually exists. */
const syncRows = () => {
  const present = inviteDialog() != null;
  for (const row of document.querySelectorAll(ROW)) {
    const group = row.closest(GROUP);
    if (group instanceof HTMLElement) group.hidden = !present;
  }
};

document.addEventListener('click', (event) => {
  const target = /** @type {Element | null} */ (event.target);
  const row = target?.closest?.(ROW);
  if (!row) return;

  const dialog = inviteDialog();
  if (!dialog || typeof dialog.showModal !== 'function') {
    syncRows();
    return;
  }
  event.preventDefault();

  // Replace, not stack (2026-08-24): the drawer swaps away while the centred pop-up is
  // open. Same class, same mechanics, same restore as the sign-in sheet's swap.
  const drawerDialog = row.closest('dialog');
  const swapped = drawerDialog instanceof HTMLDialogElement && drawerDialog.open;
  if (swapped) drawerDialog.classList.add('bokja-signin-open');

  if (!dialog.open) dialog.showModal();

  dialog.addEventListener(
    'close',
    () => {
      if (swapped) drawerDialog.classList.remove('bokja-signin-open');
      if (row instanceof HTMLElement) row.focus();
    },
    { once: true }
  );
});

const init = () => {
  syncRows();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
